<template>
  <v-app>
    <v-main>
      <keep-alive>
        <router-view :key="routeViewKey" />
      </keep-alive>
    </v-main>

    <v-overlay :value="refreshing">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <MessageDialog ref="messageDialog"></MessageDialog>
    <ConfirmDialog ref="confirmDialog"></ConfirmDialog>
  </v-app>
</template>

<script>
import Vue from "vue";
import Dexie from "dexie";

import { Plugins } from "@capacitor/core";

import mixins from "@/plugins/mixins";
import swUpdates from "@/plugins/swUpdates";

import MessageDialog from "@/dialogs/MessageDialog";
import ConfirmDialog from "@/dialogs/ConfirmDialog";

import { wsReconReq, wsCheckTryReConnect } from "@/modules/wsNotify";
import { openWebSocket } from "@/modules/app/ws.js";
import mApp from "@/modules/app";
import mDialog from "@/modules/dialog";
import mRest from "@/modules/rest";
import mStore from "@/modules/store";

const { StatusBar } = Plugins;

export default {
  name: "App",
  mixins: [mixins, swUpdates],

  components: {
    MessageDialog,
    ConfirmDialog,
  },

  data: () => ({
    refreshing: false,

    reloadMenuInterval: undefined,
    checkWsConnectionInterval: undefined,
  }),

  async created() {
    console.log("app created");
    console.log("userAgent", navigator.userAgent);
    console.log("window", window);

    // ======================================================
    // Global error boundary - catch crashes instead of white screen
    // ======================================================
    // Catch unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      console.error("[App] Unhandled promise rejection:", event.reason);
      // Prevent the default (console error only, don't crash to white)
      event.preventDefault();
    });

    // Catch global JS errors that would otherwise crash the Vue app
    window.addEventListener("error", (event) => {
      console.error("[App] Global error:", event.error || event.message);
      // If the error is from a script loading failure (common after suspension),
      // do NOT prevent default - let it fail gracefully
      if (event.target && (event.target.tagName === "SCRIPT" || event.target.tagName === "LINK")) {
        console.warn("[App] Resource load error, may retry on next resume");
        return;
      }
    });

    // Listen for appResumed event from wsNotify to refresh critical state
    document.addEventListener("appResumed", () => {
      console.log("[App] App resumed from suspension, verifying state...");
      // Re-verify IndexedDB connection is alive
      if (this.$idb) {
        try {
          this.$idb.open().catch((err) => {
            console.warn("[App] IndexedDB connection lost, reopening...", err);
          });
        } catch (e) {
          console.warn("[App] IndexedDB error on resume:", e);
        }
      }
      // Force a re-render check
      this.$forceUpdate();
    });

    window.addEventListener("online", this.updateOnlineStatus);
    window.addEventListener("offline", this.updateOnlineStatus);

    document.title = "Digi OrderPad";

    //test date
    /*
    console.log("ordertime text", "2023-12-04 10:14:04");
    console.log("ordertime time", Date.parse("2023-12-04 10:14:04"), Date.parse("2023-12-04 10:14:04 est"));
    console.log("now", Date.now(), Date(), Date().toString(), Date.parse(Date().toString()));
    console.log(Date.parse("sd"));

    const now = Date.now();
    console.log("now", now, Date(now).toString());    
    */

    const { Device } = Plugins;
    this.$store.state.deviceInfo = await Device.getInfo();
    console.log("deviceInfo", this.$store.state.deviceInfo);

    if (this.$store.state.deviceInfo.platform == "android") {
      //StatusBar.hide();
    } else if (this.$store.state.deviceInfo.platform == "windows") {
      /*
      let scrWidth = 1180;
      let scrHeight = 820;
      if (insideInstalledApp()) {
        // Size window after open the app
        window.resizeTo(scrWidth, scrHeight);

        window.addEventListener("resize", () => {
          window.resizeTo(scrWidth, scrHeight);
        });
      }
      */
    } else if (this.$store.state.deviceInfo.platform == "web") {
      if (this.$store.state.deviceInfo.operatingSystem == "android") {
        //
      }
    }

    let idbName = "do-pad";
    let idb = new Dexie(idbName);
    idb.version(0.1).stores({
      setting: "name",
    });
    Vue.prototype.$idb = idb;

    let key = "PAD_APPTOKEN";
    let appToken = localStorage.getItem(key);
    if (appToken) {
      appToken = JSON.parse(appToken);
    } else appToken = {};

    this.$root.confirmDialog = this.$refs.confirmDialog;
    this.$root.messageDialog = this.$refs.messageDialog;

    mDialog.confirmDialog = this.$refs.confirmDialog;
    mDialog.messageDialog = this.$refs.messageDialog;

    this.$store.state.rootName = "Home";

    this.refreshing = true;

    //let res_initApp = await mApp.initApp(appToken);
    let res_initApp = await this.$store.dispatch("PAD_INIT", appToken);
    if (res_initApp && res_initApp.result == "ok") {
      let res_store = await mStore.getStore(this.$store.state.appSessionKey);
      if (res_store && res_store.result == "ok") {
        let store = res_store.store;
        this.$store.state.companyName = store.info.companyName;
        this.$store.state.service = res_store.service;
        this.setStore(store);

        await this.$store.dispatch("PAD_GET_DATA");
        this.setTheme(this.$store.getters.padTheme);

        this.$store.dispatch("PAD_DOWNLOAD_MENU");

        // get server status
        let res_serverStatus = await mRest.getServerStatus();
        if (res_serverStatus && res_serverStatus.buildNo != null) {
          this.$store.state.serverBuildNo = res_serverStatus.buildNo;
        }

        let res_storeHours = await mStore.getStoreHours({ orderType: "dinein" });
        if (res_storeHours) this.$store.state.storeHours = res_storeHours;

        if (this.$store.getters.languages.length > 0) {
          let langId = this.getLanguageIdFromStorage();
          let lang;
          if (langId > 0) lang = this.$store.getters.languages.find(lang => lang.id == langId);
          if (lang == null) lang = this.$store.getters.languages[0];
          this.setLanguage(lang);
        }

        this.$store.commit("PAD_INIT", true);
      }

      this.reloadMenuInterval = setInterval(() => {
        if (this.$store.state.Store.store) {
          let now = new Date();
          let diffTimeMin = Math.floor((now - this.$store.state.Menu.lastUpdated) / 60 / 1000);
          if (diffTimeMin >= 1440) {
            this.$store.commit("PAD_MENU_DOWNLOAD", true);
          }

          if (this.$router.currentRoute.name == "Home" && this.$store.state.Pad.menuDownload) {
            this.$store.dispatch("PAD_DOWNLOAD_MENU");
            this.$store.commit("PAD_MENU_DOWNLOAD", false);
          }
        }
      }, 60 * 1000);

      this.checkWsConnectionInterval = setInterval(() => {
        wsCheckTryReConnect();
      }, 60 * 1000);
    } else {
      this.$store.commit("PAD_INIT", true);
      mApp.navName("Setup", { authorized: true });
    }

    this.refreshing = false;
  },

  beforeDestroy() {
    window.removeEventListener("online", this.updateOnlineStatus);
    window.removeEventListener("offline", this.updateOnlineStatus);
  },

  destroyed() {
    if (this.reloadMenuInterval != null) clearInterval(this.reloadMenuInterval);
    if (this.checkWsConnectionInterval != null) clearInterval(this.checkWsConnectionInterval);

    if (this.$store.state.deviceInfo.platform == "android") StatusBar.show();
  },

  mounted() { },

  activated() { },

  // Vue error boundary - catch render errors in any child component
  errorCaptured(err, vm, info) {
    console.error("[App] Vue error captured:", err, info);
    // Prevent the error from propagating to a white screen
    // Log it but don't crash the entire app
    return false;
  },

  computed: {
    routeViewKey() {
      return this.$route.fullPath;
    },

    _wsReconReq() {
      return wsReconReq();
    },
  },

  watch: {
    _wsReconReq(newValue) {
      //console.log("_wsReconReq", newValue);
      if (newValue === true) {
        if (this.$store.getters.storeCompanyCode && this.$store.state.Order.order.tableID) {
          console.log("Reconnect Web Socket");
          openWebSocket();
        }
      }
    },
  },

  methods: {
    updateOnlineStatus() {
      //console.log("updateOnlineStatus", navigator.onLine);

      this.$store.commit("SET_OFFLINE", !navigator.onLine);

      if (this.$store.state.isOffline) {
        this.showOfflineDialog();
      } else {
        this.hideOfflineDialog();
      }
    },
    showOfflineDialog() {
      //mDialog.messageDialog.open("You are offline. Please check your internet connection.", "", );
      console.log("Network is offline");
    },
    hideOfflineDialog() {
      // optional: close dialog or show "back online" message
      console.log("Network is online");
    }
  },
};
</script>

<style lang="scss">
@import "@/sass/variables.scss";
@import url("https://fonts.googleapis.com/css?family=Ubuntu");

$dialog-max-height: 200px;

html {
  //overflow-y: auto;
  overflow-y: hidden;
}

* {
  touch-action: manipulation;
}

.v-application {
  font-family: "Ubuntu";
}

.dialog-zoom {
  zoom: 150%;
}

.fullscreen-dialog {
  height: 100% !important;
  padding-bottom: env(safe-area-inset-bottom) !important;

  background-color: white;

  .dialog-card {
    .v-card__title {
      padding: 0px !important;
    }

    .v-card__text {
      height: 100% !important;
    }
  }

  .v-sheet.v-card {
    border-radius: 0px !important;
  }

  .v-sheet.v-card:not(.v-sheet--outlined) {
    box-shadow: none;
  }
}

.no-max-width {
  max-width: 100% !important;
}

.header {
  height: $header-height !important;
  padding-top: env(safe-area-inset-top);

  .row {
    .col {
      padding: 0px;

      .v-btn {
        height: 42px !important;
        color: var(--v-primaryText-base);

        .v-icon {
          color: var(--v-primaryText-base);
        }
      }
    }
  }
}

.header-title1 {
  font-size: 0.75em !important;
  color: var(--v-info-base) !important;
  text-align: center;
}

.header-title2 {
  font-size: 0.9em !important;
  color: var(--v-primaryText-base) !important;
  text-align: center;
}

.header-title2-feature {
  font-size: 0.9em;
  padding: 2px 5px;
  margin: 0px 5px;
  border-radius: 25px;
  background-color: var(--v-info-base);
  color: var(--v-infoText-base);
}

.app-logo {
  margin: 48px 0px;

  display: block;
  margin-left: auto;
  margin-right: auto;
}

.store-logo {
  height: 150px;
  object-fit: contain;

  margin: 5px;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.store-cover {
  max-height: 300px;
  //height: auto;
  height: 100%;
  width: 100%;
  object-fit: cover;

  display: block;
  margin-left: auto;
  margin-right: auto;
  padding-left: -12px !important;
}

.home-logo {
  @extend .store-logo;

  margin-top: 20px;
  margin-bottom: 20px;

  border-radius: 10px;
}

.horizontal-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.v-switch {
  margin-top: 0px;
  padding-top: 0px;
}

.v-switch-right {
  @extend .v-switch;

  .v-input__control .v-input__slot {
    justify-content: flex-end !important;
  }
}

.menu-card-img {
  object-fit: cover;
  width: 100%;
  border-radius: 5px;
}

.menu-row {}

.menu-col {
  padding: 0px;
  min-height: $item-img-height;
}

@media screen and (min-width: 600px) {
  .menu-row {
    padding: 8px;
  }

  .menu-col {
    padding: 8px;
  }
}

.order-call-for-request {
  padding: 16px 0px;
  color: var(--v-accent-base);
}
</style>

<style lang="scss" scoped>
.nav-btn-highlight {
  //background-color: var(--v-accentText-base) !important;
}

.v-footer {
  justify-content: center;
  padding: 10px;
}

.side-menu-footer {
  background-color: white !important;
  //background-color: transparent !important;
  margin: 1px !important;
}

.app-powerby {
  flex: 0 1 auto;
  //text-align: left;
  //float: left;
  font-size: 0.7em;
}

.app-version {
  flex: 0 1 auto;
  //text-align: right;
  //float: right;
  font-size: 0.7em;
}

.mrs-m {
  color: rgb(120, 194, 92);
  font-weight: bold;
}

.mrs-r {
  color: rgb(255, 203, 5);
  font-weight: bold;
}

.mrs-s {
  color: rgb(0, 130, 198);
  font-weight: bold;
}

.privacy-policy {
  font-size: 0.75em;
  color: black;
  background-color: lightgrey;
  margin: 15px 0px;
}

.lang-group {
  margin-top: 0px;
  padding-top: 0px;
}

.member-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.member-name {
  font-weight: normal;
  color: var(--v-accent-base);
  margin-left: 10px;
  vertical-align: middle;
}

.member-barcode {
  font-size: 2.5em;
  float: right;
  margin-right: 10px;
}

.setting-section {
  margin-bottom: 100px;
}

.address-list-dialog {
  height: 100%;
}

.idle-dialog-container {
  justify-content: center;
  padding: 10px;
  margin-bottom: 64px;
  display: flex;
  flex-direction: column;
}
</style>
